import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAsyncOperation } from "./useAsyncOperation";

export interface AsyncOperationResult<T> {
  type: "success" | "error";
  message: string;
  response?: T;
}

interface UseAsyncOperationWithResultParams {
  siteId: string;
  onSettled?: () => void;
}

export const useAsyncOperationWithResult = <T>({
  siteId,
  onSettled,
}: UseAsyncOperationWithResultParams) => {
  const queryClient = useQueryClient();
  const [operationKey, setOperationKey] = useState<string | null>(null);
  const [result, setResult] = useState<AsyncOperationResult<T> | null>(null);

  const asyncOperation = useAsyncOperation({
    siteId,
    operationKey,
  });

  useEffect(() => {
    if (!asyncOperation.data) return;

    if (asyncOperation.data.status === "completed") {
      const opResult = asyncOperation.data.result as T | undefined;
      setOperationKey(null);
      setResult({
        type: "success",
        message: "Operation completed successfully.",
        response: opResult,
      });
      queryClient.invalidateQueries(["site"]);
      onSettled?.();
    } else if (asyncOperation.data.status === "failed") {
      setOperationKey(null);
      setResult({
        type: "error",
        message: asyncOperation.data.error || "Operation failed.",
      });
      onSettled?.();
    }
  }, [asyncOperation.data, queryClient, onSettled]);

  useEffect(() => {
    if (asyncOperation.timedOut) {
      setOperationKey(null);
      setResult({
        type: "error",
        message: "Operation timed out after 15 minutes.",
      });
      onSettled?.();
    }
  }, [asyncOperation.timedOut, onSettled]);

  return {
    asyncOperation,
    operationKey,
    setOperationKey,
    result,
    setResult,
  };
};
